import { createEffect, createSignal, on, Show, splitProps, type ComponentProps } from "solid-js";
import { cn } from "~/lib/utils";

interface AvatarProps extends ComponentProps<"img"> {
  fallback?: string;
  size?: "sm" | "md" | "lg" | "xl";
}

export const sizes = { sm: "h-7 min-w-7 text-xs", md: "h-9 min-w-9 text-sm", lg: "h-12 min-w-12 text-base", xl: "h-16 min-w-16 text-lg" };

export function Avatar(props: AvatarProps) {
  const [local, rest] = splitProps(props, ["class", "fallback", "size", "src", "alt"]);
  const sizeClass = () => sizes[local.size ?? "md"];

  // Discord avatar URLs embed the avatar hash, so a stored URL 404s the moment
  // the user swaps avatars. Latch load failures and fall back to the initial,
  // so a dead link degrades to a clean glyph instead of a broken-image icon.
  // Reset the latch whenever the src changes, so a refreshed URL gets retried.
  const [errored, setErrored] = createSignal(false);
  createEffect(on(() => local.src, () => setErrored(false), { defer: true }));

  const showFallback = () => !local.src || errored();

  return (
    <Show
      when={!showFallback()}
      fallback={
        <span
          class={cn(
            "inline-flex items-center justify-center rounded-full bg-surface-4 border border-border font-semibold text-text-muted",
            sizeClass(), local.class,
          )}
        >
          {(local.fallback ?? "?")[0].toUpperCase()}
        </span>
      }
    >
      <img
        src={local.src}
        alt={local.alt ?? "avatar"}
        class={cn("rounded-full object-cover border border-border", sizeClass(), local.class)}
        {...rest}
        onError={() => setErrored(true)}
      />
    </Show>
  );
}
